import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { HiOutlineUsers, HiOutlineBookOpen, HiOutlineClipboardDocumentCheck, HiOutlineCalendarDays, HiOutlinePencilSquare, HiOutlineDocumentText, HiOutlineVideoCamera, HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import useAdminAuth from '../../../hooks/useAdminAuth';
import { friendlyDate, friendlyTimestamp, toLocalDateStr } from '../../../utils/dateFormat';

const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토'];
const CACHE_KEY = 'classmanage.dashboard.cache';
const CACHE_TTL = 5 * 60 * 1000; // 5분

const AdminDashboard = () => {
  const { filterByOwner } = useAdminAuth();
  const [loaded, setLoaded] = useState(false);
  const [userStats, setUserStats] = useState({ total: 0, teachers: 0, students: 0, assistants: 0, parents: 0 });
  const [classes, setClasses] = useState([]);
  const [todayClasses, setTodayClasses] = useState([]);
  const [messageCount, setMessageCount] = useState(0);
  const [yesterdayAtt, setYesterdayAtt] = useState([]);
  const [upcomingAssigns, setUpcomingAssigns] = useState([]);
  const [todayExams, setTodayExams] = useState([]);
  const [materialCount, setMaterialCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);
  const [logCount, setLogCount] = useState(0);
  const [todayAbsent, setTodayAbsent] = useState(0);
  const mountedRef = useRef(true);

  const applyData = (data) => {
    setUserStats(data.userStats);
    setClasses(data.classes);
    setTodayClasses(data.todayClasses);
    setMessageCount(data.messageCount);
    setYesterdayAtt(data.yesterdayAtt);
    setUpcomingAssigns(data.upcomingAssigns);
    setTodayExams(data.todayExams);
    setMaterialCount(data.materialCount);
    setVideoCount(data.videoCount);
    setLogCount(data.logCount);
    setTodayAbsent(data.todayAbsent);
    setLoaded(true);
  };

  useEffect(() => {
    mountedRef.current = true;
    // 캐시가 있으면 즉시 표시
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          applyData(data);
        }
      }
    } catch {}

    const load = async () => {
      // 사용자 통계
      const userSnap = await getDocs(collection(db, 'users'));
      const users = userSnap.docs.map((d) => d.data()).filter((u) => u.role !== 'admin');

      // 클래스
      const classSnap = await getDocs(collection(db, 'classes'));
      const allClasses = classSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = filterByOwner(allClasses);

      // 오늘 수업 (정규 + 보강)
      const today = DAY_MAP[new Date().getDay()];
      const todayDateStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
      const todayList = [];
      filtered.forEach((cls) => {
        if (cls.schedule?.some((s) => s.day === today)) {
          const todaySchedule = cls.schedule.find((s) => s.day === today);
          todayList.push({ ...cls, todayTime: todaySchedule?.time || '', todayType: '정규' });
        }
        const sup = (cls.supplements || []).find((s) => s.date === todayDateStr);
        if (sup) {
          todayList.push({ ...cls, todayTime: sup.time || '', todayType: '보강', id: cls.id + '-sup' });
        }
      });
      todayList.sort((a, b) => (a.todayTime || '').localeCompare(b.todayTime || ''));

      // 메시지 수
      const msgSnap = await getDocs(collection(db, 'adminMessages'));

      // 유저맵
      const userMap = {};
      userSnap.docs.forEach((d) => { userMap[d.id] = d.data(); });

      // 어제 결석/지각 현황
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = toLocalDateStr(yesterday);
      const attResults = [];
      for (const cls of filtered) {
        const attSnap = await getDocs(collection(db, 'classes', cls.id, 'attendance'));
        attSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.date === yesterdayStr) {
            const records = data.records || {};
            Object.entries(records).forEach(([sid, r]) => {
              const status = typeof r === 'string' ? r : r.status;
              if (status === 'absent' || status === 'late') {
                attResults.push({
                  className: cls.name, color: cls.color,
                  studentName: userMap[sid]?.name || sid, status,
                });
              }
            });
          }
        });
      }

      // 오늘 미출석 (오늘 수업 클래스에서 출석 기록 없거나 결석인 학생)
      const todayStr = toLocalDateStr(new Date());
      let absentCount = 0;
      for (const cls of todayList) {
        const realId = cls.id.replace('-sup', '');
        const attSnap = await getDocs(collection(db, 'classes', realId, 'attendance'));
        const todayAtt = attSnap.docs.find((d) => d.data().date === todayStr);
        const studentIds = cls.studentIds || [];
        if (!todayAtt) {
          absentCount += studentIds.length;
        } else {
          const records = todayAtt.data().records || {};
          studentIds.forEach((sid) => {
            const r = records[sid];
            if (!r) absentCount++;
            else {
              const status = typeof r === 'string' ? r : r.status;
              if (status === 'absent') absentCount++;
            }
          });
        }
      }

      // 마감 임박 과제 (오늘 ~ 3일 후)
      const threeDaysLater = new Date();
      threeDaysLater.setDate(threeDaysLater.getDate() + 3);
      const threeStr = toLocalDateStr(threeDaysLater);
      const assignResults = [];
      for (const cls of filtered) {
        const assignSnap = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        assignSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.dueDate && data.dueDate >= todayStr && data.dueDate <= threeStr) {
            const subs = data.submissions || {};
            const studentIds = cls.studentIds || [];
            const notSubmitted = studentIds.filter((sid) => !subs[sid] || subs[sid].status !== 'submitted').length;
            assignResults.push({
              className: cls.name, color: cls.color,
              title: data.title, dueDate: data.dueDate, notSubmitted,
              total: studentIds.length,
            });
          }
        });
      }
      assignResults.sort((a, b) => a.dueDate.localeCompare(b.dueDate));

      // 오늘 시험
      const examResults = [];
      for (const cls of filtered) {
        const gradeSnap = await getDocs(collection(db, 'classes', cls.id, 'grades'));
        gradeSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.date === todayStr) {
            examResults.push({ className: cls.name, color: cls.color, examName: data.examName });
          }
        });
      }

      // 자료 / 동영상 수
      let matCount = 0, vidCount = 0, lgCount = 0;
      for (const cls of filtered) {
        const [matSnap, vidSnap, logSnap] = await Promise.all([
          getDocs(collection(db, 'classes', cls.id, 'materials')),
          getDocs(collection(db, 'classes', cls.id, 'videos')),
          getDocs(collection(db, 'classes', cls.id, 'logs')),
        ]);
        matCount += matSnap.docs.length;
        vidCount += vidSnap.docs.length;
        lgCount += logSnap.docs.length;
      }

      const data = {
        userStats: {
          total: users.length,
          teachers: users.filter((u) => u.role === 'teacher').length,
          students: users.filter((u) => u.role === 'student').length,
          assistants: users.filter((u) => u.role === 'assistant').length,
          parents: users.filter((u) => u.role === 'parent').length,
        },
        classes: filtered,
        todayClasses: todayList,
        messageCount: msgSnap.docs.length,
        yesterdayAtt: attResults,
        upcomingAssigns: assignResults,
        todayExams: examResults,
        materialCount: matCount,
        videoCount: vidCount,
        logCount: lgCount,
        todayAbsent: absentCount,
      };
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() })); } catch {}
      if (mountedRef.current) applyData(data);
    };
    load();
    return () => { mountedRef.current = false; };
  }, []);

  if (!loaded) return (
    <Container>
      <SpinnerWrap>
        <Spinner />
        <SpinnerText>데이터를 불러오는 중...</SpinnerText>
      </SpinnerWrap>
    </Container>
  );

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>운영 대시보드</PageTitle>
          <PageDate>오늘 {friendlyTimestamp(new Date())}</PageDate>
        </div>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatLabel>전체 사용자</StatLabel>
          <StatValue>{userStats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>전체 클래스</StatLabel>
          <StatValue>{classes.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>오늘 수업</StatLabel>
          <StatValue>{todayClasses.length}</StatValue>
        </StatCard>
        <StatCard $alert={todayAbsent > 0}>
          <StatLabel>오늘 미출석</StatLabel>
          <StatValue>{todayAbsent}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>오늘 시험</StatLabel>
          <StatValue>{todayExams.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>업로드 문서</StatLabel>
          <StatValue>{materialCount}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>업로드 동영상</StatLabel>
          <StatValue>{videoCount}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>수업일지</StatLabel>
          <StatValue>{logCount}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>공지 메시지</StatLabel>
          <StatValue>{messageCount}</StatValue>
        </StatCard>
      </StatsGrid>

      <Section>
        <SectionTitle>오늘의 수업 현황 ({DAY_MAP[new Date().getDay()]}요일)</SectionTitle>
        <TableCard>
          {todayClasses.length === 0 ? (
            <Empty>오늘 예정된 수업이 없습니다</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>시간</Th>
                  <Th>클래스</Th>
                  <Th>강사</Th>
                  <Th>강의실</Th>
                  <Th>학생 수</Th>
                </tr>
              </thead>
              <tbody>
                {todayClasses.map((cls) => (
                  <tr key={cls.id}>
                    <Td>{cls.todayTime || '-'}</Td>
                    <Td><strong>{cls.name}</strong>{cls.todayType === '보강' && <SupBadge>보강</SupBadge>}</Td>
                    <Td>{cls.teacherName || '-'}</Td>
                    <Td>{cls.room || '-'}</Td>
                    <Td>{cls.students || 0}명</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableCard>
      </Section>

      <TwoCol>
        <Section>
          <SectionTitle>어제 결석 / 지각 현황</SectionTitle>
          <Card>
            {yesterdayAtt.length === 0 ? (
              <Empty>어제 결석/지각 학생이 없습니다</Empty>
            ) : (
              yesterdayAtt.map((item, i) => (
                <AttRow key={i}>
                  <AttStatus $status={item.status}>{item.status === 'absent' ? '결석' : '지각'}</AttStatus>
                  <AttName>{item.studentName}</AttName>
                  <AttClass><ColorDot $color={item.color} />{item.className}</AttClass>
                </AttRow>
              ))
            )}
          </Card>
        </Section>

        <Section>
          <SectionTitle>마감 임박 과제</SectionTitle>
          <Card>
            {upcomingAssigns.length === 0 ? (
              <Empty>3일 이내 마감 과제가 없습니다</Empty>
            ) : (
              upcomingAssigns.map((item, i) => (
                <AssignRow key={i}>
                  <AssignInfo>
                    <AssignTitle>{item.title}</AssignTitle>
                    <AssignMeta><ColorDot $color={item.color} />{item.className}</AssignMeta>
                  </AssignInfo>
                  <AssignRight>
                    <AssignDue $urgent={item.dueDate === toLocalDateStr(new Date())}>{friendlyDate(item.dueDate)}</AssignDue>
                    <AssignNotSub>미제출 {item.notSubmitted}/{item.total}</AssignNotSub>
                  </AssignRight>
                </AssignRow>
              ))
            )}
          </Card>
        </Section>
      </TwoCol>

      <TwoCol>
        <Section>
          <SectionTitle>사용자 분포</SectionTitle>
          <Card>
            {[
              { key: 'teachers', label: '강사' },
              { key: 'students', label: '학생' },
              { key: 'assistants', label: '조교' },
              { key: 'parents', label: '학부모' },
            ].map(({ key, label }) => {
              const count = userStats[key] || 0;
              const pct = userStats.total > 0 ? Math.round((count / userStats.total) * 100) : 0;
              return (
                <BarRow key={key}>
                  <BarLabel>{label}</BarLabel>
                  <BarTrack><BarFill style={{ width: `${pct}%` }} /></BarTrack>
                  <BarValue>{count}명 ({pct}%)</BarValue>
                </BarRow>
              );
            })}
          </Card>
        </Section>

        <Section>
          <SectionTitle>클래스별 학생 수</SectionTitle>
          <Card>
            {classes.length === 0 ? (
              <Empty>등록된 클래스가 없습니다</Empty>
            ) : (
              classes.map((cls) => (
                <BarRow key={cls.id}>
                  <BarLabel>{cls.name}</BarLabel>
                  <BarTrack><BarFill $color={cls.color} style={{ width: `${Math.min((cls.students || 0) * 5, 100)}%` }} /></BarTrack>
                  <BarValue>{cls.students || 0}명</BarValue>
                </BarRow>
              ))
            )}
          </Card>
        </Section>
      </TwoCol>
    </Container>
  );
};

export default AdminDashboard;

const Container = styled.div``;
const SpinnerWrap = styled.div`display:flex;flex-direction:column;align-items:center;justify-content:center;padding:120px 0;`;
const Spinner = styled.div`
  width:36px;height:36px;border:3px solid #E5E7EB;border-top-color:#4A7CFE;border-radius:50%;
  animation:spin 0.8s linear infinite;
  @keyframes spin{to{transform:rotate(360deg);}}
`;
const SpinnerText = styled.p`font-size:14px;color:#9CA3AF;margin-top:14px;`;
const PageHeader = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;`;
const PageDate = styled.p`font-size:13px;color:#9CA3AF;margin-top:2px;`;

const StatsGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:12px;margin-bottom:24px;`;
const StatCard = styled.div`background:${({ $alert }) => $alert ? '#EF4444' : '#4A7CFE'};border-radius:4px;padding:20px;`;
const StatLabel = styled.p`font-size:16px;color:rgba(255,255,255,0.9);margin-bottom:8px;font-weight:600;text-align:center;`;
const StatValue = styled.p`font-size:36px;font-weight:800;color:#fff;text-align:center;`;

const Section = styled.div`margin-bottom:24px;display:flex;flex-direction:column;`;
const SectionTitle = styled.h2`font-size:16px;font-weight:700;color:#111827;margin-bottom:12px;`;

const TableCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;overflow:hidden;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`text-align:left;padding:12px 16px;font-size:12px;font-weight:600;color:#9CA3AF;background:#F9FAFB;border-bottom:1px solid #EAEDF3;`;
const Td = styled.td`padding:12px 16px;font-size:13px;color:#374151;border-bottom:1px solid #F3F4F6;strong{font-weight:600;}`;
const Empty = styled.p`padding:32px;text-align:center;color:#9CA3AF;font-size:14px;`;

const TwoCol = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px;align-items:stretch;@media(max-width:1000px){grid-template-columns:1fr;}`;
const Card = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;padding:20px;flex:1;box-sizing:border-box;`;
const SupBadge = styled.span`font-size:11px;font-weight:600;color:#DC2626;background:#FEE2E2;padding:2px 6px;border-radius:4px;margin-left:6px;`;
const BarRow = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:14px;&:last-child{margin-bottom:0;}`;
const BarLabel = styled.span`font-size:13px;font-weight:600;color:#374151;min-width:80px;`;
const BarTrack = styled.div`flex:1;height:20px;background:#F3F4F6;border-radius:10px;overflow:hidden;`;
const BarFill = styled.div`height:100%;background:${({ $color }) => $color || '#4A7CFE'};border-radius:10px;transition:width 0.3s;min-width:2px;`;
const BarValue = styled.span`font-size:13px;color:#6B7280;min-width:80px;text-align:right;`;

const ColorDot = styled.span`display:inline-block;width:8px;height:8px;border-radius:50%;background:${({ $color }) => $color || '#9CA3AF'};flex-shrink:0;`;

/* 어제 결석/지각 */
const AttRow = styled.div`display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid #F3F4F6;&:last-child{border-bottom:none;}`;
const AttStatus = styled.span`font-size:12px;font-weight:700;padding:2px 8px;border-radius:8px;min-width:36px;text-align:center;
  background:${({ $status }) => $status === 'absent' ? '#FEE2E2' : '#FEF3C7'};
  color:${({ $status }) => $status === 'absent' ? '#DC2626' : '#CA8A04'};`;
const AttName = styled.span`font-size:14px;font-weight:600;color:#111827;flex:1;`;
const AttClass = styled.span`display:flex;align-items:center;gap:4px;font-size:12px;color:#6B7280;`;

/* 마감 임박 과제 */
const AssignRow = styled.div`display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #F3F4F6;&:last-child{border-bottom:none;}`;
const AssignInfo = styled.div`flex:1;`;
const AssignTitle = styled.div`font-size:14px;font-weight:600;color:#111827;margin-bottom:3px;`;
const AssignMeta = styled.div`display:flex;align-items:center;gap:4px;font-size:12px;color:#6B7280;`;
const AssignRight = styled.div`text-align:right;`;
const AssignDue = styled.div`font-size:12px;font-weight:600;color:${({ $urgent }) => $urgent ? '#DC2626' : '#374151'};`;
const AssignNotSub = styled.div`font-size:11px;color:#F59E0B;margin-top:2px;`;
